import type { CSSProperties, ReactNode } from 'react'
import { useCallback, useMemo, useState } from 'react'

import { formatDurationHuman } from '@/utils'
import { useAtomValue } from 'jotai'

import { CueRunState } from '~shared/utils'
import type { Timestamp } from '~shared/utils'

import { momentAtom } from '@/store/moment.store'

import { DurationPopover } from './DurationPopover'
import { DurationPopoverContent } from './DurationPopoverContent'
import { DurationTopDisplay } from './DurationTopDisplay'
import type { TimingValues } from './timing.types'
import { TimingScaffold } from './TimingScaffold'

import type { Moment } from '@/utils'

type DurationProps = {
  values: TimingValues
  setValues: (update: Partial<TimingValues>) => void
  cellStyle?: CSSProperties
  readonly?: boolean
  loading?: boolean
  timestamp?: Timestamp | null
  timezone: string
  below?: ReactNode
}

/**
 * Duration Component
 *
 * Pure component for displaying and editing a cue's duration. Manages its own
 * internal editing state but triggers external updates via setValues.
 */
export function Duration({
  values,
  setValues,
  cellStyle,
  readonly,
  loading,
  timestamp,
  below,
}: DurationProps) {
  const [open, setOpen] = useState(false)
  const [duration, setDuration] = useState(values.duration)

  const moment = useAtomValue(momentAtom)

  // Sync internal state when external cue duration changes
  // This implements the "last-action-wins" conflict management
  const [syncedDuration, setSyncedDuration] = useState(values.duration)
  if (values.duration !== syncedDuration) {
    setSyncedDuration(values.duration)
    setDuration(values.duration)
  }

  // Save changes to duration
  // Only update if the duration has actually changed
  const handlePopoverSave = useCallback(() => {
    if (duration === values.duration) return
    setValues({ duration })
  }, [duration, values.duration, setValues])

  // Cancel changes and reset to current cue duration
  const handlePopoverCancel = useCallback(() => {
    setDuration(values.duration)
  }, [values.duration])

  // Combined calculation of duration display text and styling
  const btnMarkup = useMemo(
    () => getButtonMarkup(duration, timestamp, moment),
    [duration, timestamp, moment],
  )

  if (!timestamp) return null

  return (
    <TimingScaffold
      style={cellStyle}
      loading={loading}
      below={below}
      top={
        <DurationTopDisplay
          cueState={timestamp.state}
          actualDuration={timestamp.actual.duration}
          originalDuration={timestamp.original.duration}
          plannedDuration={values.duration}
        />
      }
    >
      <DurationPopover
        open={open}
        setOpen={setOpen}
        onSave={handlePopoverSave}
        onCancel={handlePopoverCancel}
        buttonLabel={btnMarkup.label}
        buttonClassName={btnMarkup.className}
        readonly={readonly}
      >
        <DurationPopoverContent
          duration={duration}
          setDuration={setDuration}
          disabled={readonly}
          cueState={timestamp.state}
          originalDuration={timestamp.original.duration}
          actualDuration={timestamp.actual.duration}
        />
      </DurationPopover>
    </TimingScaffold>
  )
}

/**
 * Generates button markup (label and styling) based on duration, timestamp and
 * moment
 *
 * @param duration - The planned duration
 * @param timestamp - The timestamp object with state and actual duration
 * @param moment - The moment object with left property
 *
 * @returns - Object with label and className properties
 */
function getButtonMarkup(
  duration: number,
  timestamp: Timestamp | null | undefined,
  moment: Moment | null,
) {
  // Base styles that appear in all cases
  const baseStyle = 'text-center tabular-nums'
  const formatOptions = { prefix: '+', omitZero: true }

  // Early return for missing data (use Math.round for projected/planned durations)
  if (!timestamp || !moment) {
    return {
      label: formatDurationHuman(duration, {
        ...formatOptions,
        roundingFn: Math.round,
      }),
      className: baseStyle,
    }
  }

  // Set up variables for our result
  let durationToFormat = duration
  let colorClass = ''
  let roundingFn = Math.round // Default for completed/projected durations

  // Determine what to display based on cue state
  switch (timestamp.state) {
    case CueRunState.CUE_ACTIVE:
      durationToFormat = moment.left
      roundingFn = Math.ceil // Countdown: round up (conservative)

      // Red for overtime
      if (moment.left <= -1000) colorClass = 'text-red-600'
      break

    case CueRunState.CUE_PAST:
      durationToFormat = timestamp.actual.duration
      roundingFn = Math.round // Completed: round to match visual time display

      // Green for under duration (faster than planned)
      if (duration > timestamp.actual.duration) colorClass = 'text-green-600'
      // Red for over duration (slower than planned)
      else if (duration < timestamp.actual.duration) colorClass = 'text-red-600'
      break

    default:
      // Future cues: use Math.round for projected durations
      durationToFormat = timestamp.actual?.duration || duration
      roundingFn = Math.round
  }

  // Return formatted label and appropriate className
  return {
    label: formatDurationHuman(durationToFormat, {
      ...formatOptions,
      roundingFn,
    }),
    className: [baseStyle, colorClass].filter(Boolean).join(' '),
  }
}
